import React, { useEffect, useMemo, useRef, useState } from "react";
import { makeStyles } from "@material-ui/core/styles";
import { useMutation, useQuery } from "@apollo/client";
import { DateTime } from "luxon";
import { isPossiblePhoneNumber, parsePhoneNumber } from "react-phone-number-input";

import { T } from "../../constants";
import { useUi, cx } from "../../ui";
import ModalCard from "../../components/ModalCard";
import FieldRow from "../../components/FieldRow";
import PhoneField from "../../components/PhoneField";
import DepartureSection from "./DepartureSection";
import ArrivalSection from "./ArrivalSection";
import { ARRIVAL } from "../../arrival";
import { fromLocalInput } from "../../departure";
import { nowIso } from "../../dayTime";
import { createConciergeBooking, updateConciergeBooking } from "../queries";
import { bookingContactsQuery } from "../../vip-routing/queries";
import { allActiveRateCards, getStripePaymentMethods, rateCardById } from "../../../Booking/queries";
import {
  format24HPeriod,
  formatCurrency,
  formatMinutes,
  makeSafe,
  requirements,
} from "../../../../lib/rateCardUtils";
import useBookingQuote from "../useBookingQuote";
import useDeparture from "../useDeparture";
import useArrival from "../useArrival";
import { toFriendlyBookingError } from "../../../../lib/bookingErrors";
import { formatPlate, fmtDate } from "../../../../lib/misc";
import drivoApollo from "../../../../lib/drivoApollo";
import CardInput from "./CardInput";
import LoadingIndicator from "../../../../components/LoadingIndicator";
import { useInstallation } from "../../../../components/InstallationContext";
import { useAppConfig } from "../../../../components/AppConfigContext";
import {isPaymentRequired, mapBookingToArrival, pagedVipContactIds, vipContactIds as bookingVipContactIds} from "../../helpers";
import VipContactPicker from "./VipContactPicker";

/*
 * Every guest field a rate card can demand, in the order they read best at the
 * desk. `requirement` is the flag on requirements(rateCard) that decides
 * whether the field is shown AND mandatory — the rate card is the single
 * source of truth, exactly as on the full booking form.
 */
const GUEST_FIELDS = [
  { key: "guestName", label: "Guest name", placeholder: "e.g. Sarah Chen" },
  { key: "guestPhone", label: "Mobile", phone: true, placeholder: "4XX XXX XXX" },
  { key: "guestEmail", label: "Email", type: "email", placeholder: "guest@example.com" },
  { key: "guestRoom", label: "Room" },
  { key: "guestApartmentNumber", label: "Apartment number" },
  { key: "guestReservation", label: "Reservation" },
  { key: "guestCompany", label: "Company" },
  { key: "guestDepartment", label: "Department" },
  { key: "guestEmployeeCode", label: "Employee code" },
  { key: "guestPosition", label: "Position" },
  { key: "carType", label: "Vehicle type", requirement: "includeCarType" },
  { key: "carNotes", label: "Vehicle notes", requirement: "includeCarNotes" },
];

const EMPTY_GUEST = GUEST_FIELDS.reduce((acc, f) => ({ ...acc, [f.key]: "" }), {});

const useStyles = makeStyles({
  grid2: { display: "grid", gridTemplateColumns: "repeat(2, minmax(0, 1fr))", gap: "0 14px" },
  small: { padding: "8px 12px", fontSize: 13 },
  plateInput: { textTransform: "uppercase" },
  tiles: { display: "flex", gap: 8, marginBottom: 14 },
  tile: {
    flex: 1,
    border: `1px solid ${T.border}`,
    background: T.elevated,
    borderRadius: 8,
    padding: "10px 12px",
    textAlign: "left",
    cursor: "pointer",
    fontFamily: "inherit",
    color: T.fg,
    "&[aria-pressed='true']": { borderColor: T.primary, background: T.accent },
  },
  tileTitle: { fontWeight: 600, fontSize: 14 },
  tileSub: { fontSize: 12, color: T.mutedFg },
  check: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    fontSize: 13,
    color: T.fg,
    marginBottom: 12,
    cursor: "pointer",
  },
  checkbox: { width: 15, height: 15, accentColor: T.primary, flexShrink: 0 },
  meta: { fontSize: 11, color: T.mutedFg },
  dangerNote: { margin: "4px 0 0", fontSize: 11, color: T.danger },
  warningNote: { margin: "8px 0 0", fontSize: 11, color: T.warningFg },
  errorBanner: { borderRadius: 6, border: `1px solid ${T.danger}`, background: T.dangerTint, padding: "10px 12px", fontSize: 12, color: T.fg, marginBottom: 14 },
  /* Holds the body's height so the modal does not jump once the cards land. */
  loading: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    minHeight: 220,
  },
  price: { fontSize: 15, fontWeight: 600, color: T.fg, padding: "6px 0" },
  priceMeta: { fontSize: 12, fontWeight: 400, color: T.mutedFg },
  priceStale: { opacity: 0.45 },
  vipRouting: { marginTop: 8, display: "flex", flexDirection: "column", gap: 6 },
});

/*
 * Rate cards of the same lineage share a name, so disambiguate by version the
 * way the full booking form does.
 */
export function rateCardOptions(rateCards) {
  const byId = {};
  rateCards.forEach((card) => {
    byId[card.id] = card;
  });

  const lineageCounts = {};
  Object.values(byId).forEach(({ lineageId }) => {
    lineageCounts[lineageId] = (lineageCounts[lineageId] || 0) + 1;
  });

  return Object.values(byId)
    .map((card) => ({
      value: card.id,
      label:
        lineageCounts[card.lineageId] > 1
          ? `${card.name} (version ${card.version})`
          : card.name,
    }))
    .sort((a, b) => a.label.localeCompare(b.label));
}

export function toCountryCode(country) {
  return typeof country === "string" && country.length === 2
    ? country.toUpperCase()
    : undefined;
}

export function toE164(raw, country) {
  if (!raw) return "";

  return parsePhoneNumber(raw, country)?.number ?? raw;
}

export function today(timeZone) {
  const dt = timeZone ? DateTime.now().setZone(timeZone) : DateTime.now();

  return (dt.isValid ? dt : DateTime.now()).toFormat("yyyy-MM-dd");
}

/* Whole day in the facility's zone, which is how both date inputs and the
 * booking mutations read a booking window. */
export function toDay(iso, timeZone) {
  if (!iso) return null;

  const dt = DateTime.fromISO(iso, timeZone ? { zone: timeZone } : undefined);

  return dt.isValid ? dt.toFormat("yyyy-MM-dd") : null;
}

/**
 * A card that prices from the real entry and exit can afford not to know when
 * the guest leaves; every other card sells a departure date, so an unknown one
 * cannot be priced.
 */
export function allowsUnknownDeparture(rateCard) {
  return rateCard?.type === "HOURLY" || rateCard?.type === "TIERED_HOURS";
}

export default function QuickBookingModal({
  onClose,
  onCreated,
  onSaved,
  initial,
  mode = "create",
}) {
  const s = useStyles();
  const ui = useUi();
  /* Read from context rather than a prop: the modal is rendered both from the
   * dashboard and from DetectedView, two levels deeper. */
  const { installation, facility } = useInstallation();
  const appConfig = useAppConfig();

  const isEdit = mode === "edit";
  /* The arrival and the guest card stop being decisions the moment the guest is
   * on site: the arrival has happened, and the card has already priced and
   * parked the stay. Only a booking still waiting for its guest can move them. */
  const arrivalSettled = isEdit && initial?.bookingStatus !== "PENDING_ENTRY";
  const tz = installation?.timeZoneName;

  /* One instant for the whole session at the desk. Re-reading the clock on
   * every render would slide the pickers' floor under the operator's cursor. */
  const now = useMemo(() => nowIso(tz), [tz]);

  const [plate, setPlate] = useState(initial?.plate ?? "");
  const [rateCard, setRateCard] = useState(null);
  /* Whole days as yyyy-MM-dd, both defaulting to today — the same shape and
   * the same defaults the full booking form uses. Times come from the rate
   * card's entryAfterTime/exitBeforeTime, never from the operator. */
  const [startDay, setStartDay] = useState(
    () => (isEdit && toDay(initial?.checkIn, tz)) || today(tz),
  );
  const [endDay, setEndDay] = useState(
    () => (isEdit && toDay(initial?.checkOut, tz)) || today(tz),
  );

  const countryCode = toCountryCode(facility?.city?.country);
  const [guest, setGuest] = useState(() => {
    const base = {
      ...EMPTY_GUEST,
      ...(isEdit
        ? initial?.guest
        : {
            guestName: initial?.guestName ?? "",
            guestPhone: initial?.phone ?? "",
            guestEmail: initial?.email ?? "",
          }),
    };

    return { ...base, guestPhone: toE164(base.guestPhone, countryCode) };
  });
  /* The quote reports the booking's expiry, which bounds the departure picker,
   * but the quote is itself priced from the chosen departure. Holding the last
   * reported expiry in state breaks that cycle; a render's lag is harmless for
   * a bound that only applies to cards whose window cannot grow anyway. */
  const [quoteExpiresAt, setQuoteExpiresAt] = useState(null);
  const [paymentMode, setPaymentMode] = useState(
    initial?.payLater === true ? "payLater" : "onsite",
  );
  const [reasons, setReasons] = useState(initial?.vip ? ["VIP"] : []);
  const [submitError, setSubmitError] = useState(null);

  const [vip, setVip] = useState(Boolean(initial?.vip));
  const [vipContactIds, setVipContactIds] = useState(() =>
    bookingVipContactIds(initial),
  );

  const { data: contactData } = useQuery(bookingContactsQuery, {
    variables: { installationId: installation?.id },
    skip: !installation?.id,
    fetchPolicy: "no-cache",
  });
  const contacts = contactData?.contacts ?? [];

  const [stripeCustomerId, setStripeCustomerId] = useState(
    isEdit ? initial?.stripeCustomerId ?? null : null,
  );
  const [paymentMethod, setPaymentMethod] = useState(null);
  const [cardRefresh, setCardRefresh] = useState(0);
  /* Set the moment Stripe confirms the setup intent. The card lookup below is
   * only for *displaying* the card, so submission must not wait on it — the
   * card is already attached to the customer by then. */
  const [cardConfirmedThisSession, setCardConfirmedThisSession] = useState(false);

  const { data: rateCardData, loading: rateCardsLoading, error: rateCardsError } =
    useQuery(allActiveRateCards, {
      variables: { installationId: installation?.id },
      skip: !installation?.id,
      fetchPolicy: "no-cache",
    });

  const arrivalListQueries = {
    refetchQueries: ["expectedArrivals", "valetIntakeCandidates"],
    awaitRefetchQueries: true,
  };

  const [createBooking, { loading: creating }] = useMutation(
    createConciergeBooking,
    arrivalListQueries,
  );
  const [updateBooking, { loading: saving }] = useMutation(
    updateConciergeBooking,
    arrivalListQueries,
  );
  const submitting = creating || saving;

  const allActiveCards = rateCardData?.allRateCards ?? [];

  /* Preselect the booking's own guest card once the list arrives. A card that
   * is no longer active is not in it, so the operator has to pick a new one. */
  const bookingRateCardId = isEdit ? initial?.rateCardId : null;

  /* Once the guest is on site the card cannot be changed, so an archived one
   * has to be fetched by id rather than repicked - otherwise the form has no
   * card at all and a guest or departure edit cannot be saved. */
  const archivedCardId =
    arrivalSettled &&
    bookingRateCardId &&
    !rateCardsLoading &&
    !allActiveCards.some((card) => card.id === bookingRateCardId)
      ? bookingRateCardId
      : null;

  const { data: archivedCardData, loading: archivedCardLoading } = useQuery(
    rateCardById,
    {
      variables: { rateCardId: archivedCardId },
      skip: !archivedCardId,
      fetchPolicy: "no-cache",
    },
  );
  const archivedCard =
    archivedCardData?.rateCard?.id === archivedCardId
      ? archivedCardData.rateCard
      : null;

  const rateCards = useMemo(() => {
    const valetCards = allActiveCards.filter((card) => card.isValet);

    if (
      bookingRateCardId &&
      !valetCards.some((card) => card.id === bookingRateCardId)
    ) {
      const existing =
        allActiveCards.find((card) => card.id === bookingRateCardId) ??
        archivedCard;
      if (existing) return [...valetCards, existing];
    }

    return valetCards;
  }, [allActiveCards, archivedCard, bookingRateCardId]);

  const options = useMemo(() => rateCardOptions(rateCards), [rateCards]);
  const [rateCardPreselected, setRateCardPreselected] = useState(false);

  useEffect(() => {
    if (!bookingRateCardId || rateCardPreselected || rateCards.length === 0) return;
    /* Settling on "retired, pick another" before the booking's own card has
     * landed would strand a settled booking on a prompt it cannot answer. */
    if (archivedCardLoading) return;

    setRateCard(rateCards.find((c) => c.id === bookingRateCardId) ?? null);
    setRateCardPreselected(true);
  }, [archivedCardLoading, bookingRateCardId, rateCardPreselected, rateCards]);

  /* A new booking opens on the first card in the list rather than on a prompt
   * to pick one - it is the common case, and everything below the select needs
   * a card before it can say anything. An edit keeps its own card, and is left
   * on the prompt when that card has been retired. */
  useEffect(() => {
    if (isEdit || rateCard || options.length === 0) return;

    setRateCard(rateCards.find((c) => c.id === options[0].value) ?? null);
  }, [isEdit, rateCard, options, rateCards]);

  const rateCardMissing =
    Boolean(bookingRateCardId) && rateCardPreselected && !rateCard;

  const vipRateCardId = rateCard?.isVip ? rateCard.id : null;

  useEffect(() => {
    if (vipRateCardId && !isEdit) setVip(true);
  }, [vipRateCardId, isEdit]);

  /* The card's own contact, pre-selected so the routing a card carries is the
   * default rather than something to remember. Only ticked, not locked - the
   * concierge can drop it, and the server honours the list this form sends. */
  const cardVipContactId = rateCard?.defaultVipContactId ?? null;

  const defaultedCardId = useRef((isEdit && initial?.isVip && initial?.rateCardId) || null);

  useEffect(() => {
    if (!vip || !rateCard?.id) return;
    if (defaultedCardId.current === rateCard.id) return;

    defaultedCardId.current = rateCard.id;
    /* Paged contacts have already been alerted, so they survive the reset. */
    setVipContactIds(
      [...pagedVipContactIds(initial), cardVipContactId].filter(
        (id, i, ids) => id && ids.indexOf(id) === i,
      ),
    );
  }, [cardVipContactId, vip, rateCard?.id]);

  const required = rateCard ? requirements(rateCard) : {};
  const visibleFields = GUEST_FIELDS.filter(
    (f) => required[f.requirement ?? f.key],
  );

  const isMonthly = rateCard?.type === "MONTHLY";
  const isFixedPackage =
    rateCard?.type === "DAILY" &&
    rateCard?.calculationMode === "MULTI_DATE_FIXED_PACKAGE";
  /* SINGLE_DAY and MONTHLY cards derive their exit from the entry, so there is
   * no exit picker to show. */
  const isSingleDay =
    rateCard?.calculationMode === "SINGLE_DAY" || rateCard?.type === "MONTHLY";

  // Auto-snap dates to month boundaries for MONTHLY rate cards.
  useEffect(() => {
    if (!isMonthly || !startDay) return;

    const dt = DateTime.fromISO(startDay, { zone: installation.timeZoneName });
    if (!dt.isValid) return;

    setStartDay(dt.startOf("month").toFormat("yyyy-MM-dd"));
    setEndDay(dt.endOf("month").toFormat("yyyy-MM-dd"));
  }, [isMonthly, startDay, installation]);

  /* Auto-fill and lock dates from the rate card for MULTI_DATE_FIXED_PACKAGE.
   * The API enforces this on save too — locking the UI just prevents drift. */
  useEffect(() => {
    if (!isFixedPackage) return;

    const { fixedBookingStartDate, fixedBookingEndDate } = rateCard;
    if (!fixedBookingStartDate || !fixedBookingEndDate) return;

    setStartDay(fixedBookingStartDate);
    setEndDay(fixedBookingEndDate);
  }, [isFixedPackage, rateCard]);

  /* The booking window sent to the server and priced in the preview: whole
   * days offset by the rate card's entry/exit times, identical to
   * pages/Booking/Create.js. */
  const { startDateTime, endDateTime } = useMemo(() => {
    const tz = installation?.timeZoneName;
    if (!rateCard || !tz) return {};

    const toIso = (day, minutes) =>
      day
        ? DateTime.fromISO(day, { zone: tz }).plus({ minutes }).toISO() || undefined
        : undefined;

    return {
      startDateTime: toIso(startDay, rateCard.entryAfterTime),
      endDateTime: toIso(endDay, rateCard.exitBeforeTime),
    };
  }, [installation, rateCard, startDay, endDay]);

  /* When the guest says they will leave, plus the exit date that follows from
   * it. Owns its own state and writes endDay through the setter — see
   * useDeparture for why the two dates are kept apart. */
  const departure = useDeparture({
    rateCard,
    tz,
    initial,
    startDay,
    endDay,
    setEndDay,
    startDateTime,
    endDateTime,
    expiresAt: quoteExpiresAt,
    requireExact: true,
  });
  /* The one value the mutations carry; everything else the section owns. */
  const { expectedDepartureAt } = departure;

  const arrival = useArrival({
    rateCard,
    tz,
    initial,
    startDay,
    setStartDay,
    endDay,
    setEndDay,
    startDateTime,
    endDateTime,
    isEdit,
    departure,
    requireExact: true,
  });
  const { expectedArrivalAt, mode: arrivalMode } = arrival;

  const setGuestField = (key, value) =>
    setGuest((prev) => ({ ...prev, [key]: value }));

  const notifyReasons = () =>
    vip && !reasons.includes("VIP") ? [...reasons, "VIP"] : reasons;

  const missingGuestField = visibleFields.some((f) => !guest[f.key].trim());

  const badPhone = visibleFields.some(
    (f) => f.phone && guest[f.key] && !isPossiblePhoneNumber(guest[f.key]),
  );

  /* Taking payment at the desk means capturing a card, which only exists for
   * guest cards flagged paymentStripe. Every other card is settled elsewhere,
   * so "Paid on-site" is not an option for them. */
  const needsStripe = Boolean(rateCard) && isPaymentRequired(rateCard);
  const settledElsewhere = Boolean(rateCard) && !needsStripe;
  const allowPayLater = needsStripe && rateCard?.valetPayLater === true;
  const payingLater = allowPayLater && paymentMode === "payLater";
  const showCardInput = needsStripe && paymentMode === "onsite";
  // A booking being edited already has its card attached to the customer.
  const hasCard = Boolean(
    paymentMethod || cardConfirmedThisSession || (isEdit && stripeCustomerId),
  );

  const paymentLocked = isEdit;

  /* Both figures the desk needs, priced by the same module that will charge —
   * see useBookingQuote. Nothing is synthesised in the browser. */
  const { quote, details: quotedDetails, loading: quoting, error: quoteError } =
    useBookingQuote({
      installationId: installation?.id,
      rateCardId: rateCard?.id,
      startDay: startDateTime,
      endDay: endDateTime,
      expectedDepartureAt,
      expectedArrivalAt,
    });

  useEffect(() => {
    setQuoteExpiresAt(quote?.expiresAt ?? null);
  }, [quote]);

  useEffect(() => {
    if (rateCardsLoading || !rateCard) return;
    if (!allowPayLater && paymentMode === "payLater") setPaymentMode("onsite");
  }, [rateCardsLoading, rateCard, allowPayLater, paymentMode]);

  /* Fetch the saved card purely so the operator can see what they captured. */
  useEffect(() => {
    if (!stripeCustomerId || !installation?.id) return;

    let cancelled = false;

    drivoApollo
      .query({
        query: getStripePaymentMethods,
        variables: { customerId: stripeCustomerId, installationId: installation.id },
        fetchPolicy: "no-cache",
      })
      .then(({ data }) => {
        if (!cancelled) setPaymentMethod(data?.getStripePaymentMethods?.[0] ?? null);
      })
      .catch(() => {
        /* Display-only — cardConfirmedThisSession already unblocks submission. */
      });

    return () => {
      cancelled = true;
    };
  }, [stripeCustomerId, installation, cardRefresh]);

  /*
   * The two figures the desk reads out, each on one line: what is taken at the
   * counter, and what the stay is expected to come to. Both are the server's,
   * formatted here and nowhere computed - see useBookingQuote.
   */
  const currency = quote?.currency ?? installation?.currency;
  const isHourly =
    rateCard?.type === "HOURLY" || rateCard?.type === "TIERED_HOURS";
  /* A card with no Stripe payment takes nothing at the desk; the hotel
     invoices the stay instead. */
  const hotelInvoiced = Boolean(rateCard) && rateCard.paymentStripe === false;
  const scheduled = quotedDetails?.scheduled;

  const chargedNow = payingLater ? 0 : quote?.chargedNow ?? 0;
  const estimatedTotal =
    quote?.expected ?? Number(quotedDetails?.billing?.scheduled?.fee?.total ?? NaN);
  const hasEstimate = Number.isFinite(estimatedTotal);

  const safeCard = rateCard ? makeSafe(rateCard) : null;

  const rateLine = !safeCard
    ? "Select a guest card"
    : isHourly
      ? `${formatCurrency(safeCard.bookingRateAudPerHour, currency)} per hour · ${rateCard.name}`
      : `${format24HPeriod(scheduled?.days ?? 1, rateCard)} × ${formatCurrency(
          safeCard.bookingRateAudPerDay,
          currency,
        )} · ${rateCard.name}`;

  const chargedNowHint = hotelInvoiced
    ? "Invoiced by the hotel. Nothing is taken at the desk."
    : payingLater
      ? "Nothing is taken at the desk — settled from the guest's pickup link."
      : undefined;

  const exitClock = fmtDate(scheduled?.endAt, tz, "HH:mm");

  const stayLine = !safeCard
    ? "Select a guest card"
    : isHourly
      ? "Settled at pickup on the hours actually parked"
      : `Paid until ${exitClock}${
          isFixedPackage ? "" : `, then overstay`
        }`;

  const estimatedHint = !safeCard
    ? undefined
    : isHourly
      ? `The hold covers one rate block; the real duration is priced at ${formatCurrency(
          safeCard.bookingRateAudPerHour,
          currency,
        )} per hour when the guest leaves.`
      : isFixedPackage
        ? "A fixed package: the dates and the price come from the guest card."
        : `Leaving after ${exitClock} costs ${formatCurrency(
            safeCard.overstayFlagfallAud,
            currency,
          )} plus ${formatCurrency(
            safeCard.overstayRateAudPerHour,
            currency,
          )} per hour, with ${formatMinutes(safeCard.overstayGraceMinutes)} grace.`;

  const isWalkUp = arrivalMode === ARRIVAL.NOW;

  /* The floor under each picker. A walk-up is arriving now, so its pickup is
   * measured from now; anything else is measured from the arrival the operator
   * has just chosen, which is what stops an exit landing before its entry. */
  const arrivalFloor = isEdit ? null : now;
  const arrivalInstant = isWalkUp
    ? now
    : expectedArrivalAt ?? fromLocalInput(arrival.at, tz) ?? startDateTime ?? now;

  /* Belt and braces behind the picker: a time typed into the manual field, or
   * an arrival dragged past a departure already chosen, must not get through. */
  const departureAfterArrival =
    !expectedDepartureAt ||
    !arrivalInstant ||
    Date.parse(expectedDepartureAt) > Date.parse(arrivalInstant);

  const canSubmit =
    !submitting &&
    Boolean(rateCard) &&
    (paymentLocked || !needsStripe || payingLater || (showCardInput && hasCard)) &&
    plate.trim().length >= 3 &&
    !missingGuestField &&
    !badPhone &&
    departure.valid &&
    departureAfterArrival &&
    arrival.valid &&
    Boolean(startDateTime && endDateTime);

  async function submit() {
    if (!canSubmit) return;

    setSubmitError(null);

    /* Send '' for anything this rate card doesn't ask for, matching the full
     * booking form so the server never stores a stale value. */
    const guestValues = GUEST_FIELDS.reduce(
      (acc, f) => ({
        ...acc,
        [f.key]: required[f.requirement ?? f.key] ? guest[f.key].trim() : "",
      }),
      {},
    );

    if (isEdit) return save(guestValues);

    try {
      const { data } = await createBooking({
        variables: {
          installationId: installation.id,
          rateCardId: rateCard.id,
          plate: formatPlate(plate),
          entryEventId: initial?.entryEventId ?? null,
          startDay: startDateTime,
          endDay: endDateTime,
          expectedDepartureAt,
          expectedArrivalAt,
          arrivalMode,
          stripeCustomerId: payingLater ? null : stripeCustomerId,
          payLater: payingLater,
          isVip: vip,
          vipContactIds: vip ? vipContactIds : [],

          valetSessionId: initial?.valetSessionId ?? null,

          ...guestValues,
        },
      });

      const booking = data.createBooking;

      /* Everything the booking leads to - the offsite ticket, the intake, the
       * photos - happens on the dashboard behind us. The desk gets the form
       * off the screen the moment the write lands. */
      onCreated(
        {
          ...mapBookingToArrival(booking),
          valetSessionId: booking.valetSession?.id ?? null,
        },
        notifyReasons(),
      );
    } catch (err) {
      setSubmitError(toFriendlyBookingError(err).message);
    }
  }

  /* updateBooking rewrites the row from its input, so status and the stripe
   * customer travel back untouched alongside the edited fields. */
  async function save(guestValues) {
    try {
      const { data } = await updateBooking({
        variables: {
          installationId: installation.id,
          bookingId: initial.bookingId,
          rateCardId: rateCard.id,
          plate: formatPlate(plate),
          startDay: startDateTime,
          endDay: endDateTime,
          expectedDepartureAt,
          expectedArrivalAt,
          arrivalMode,
          stripeCustomerId,
          payLater: payingLater,
          status: initial.bookingStatus,
          isVip: vip,
          vipContactIds: vip ? vipContactIds : [],

          ...guestValues,
        },
      });

      onSaved(mapBookingToArrival(data.updateBooking), notifyReasons());
    } catch (err) {
      setSubmitError(toFriendlyBookingError(err).message);
    }
  }

  const COPY = {
    create: {
      heading: "New booking",
      submit: isWalkUp ? "Add to arrivals" : "Create booking",
      busy: "Creating…",
    },
    convert: {
      heading: "Add booking details",
      submit: "Create & start intake",
      busy: "Creating…",
    },
    edit: { heading: "Edit booking", submit: "Save booking", busy: "Saving…" },
  };

  const { heading, submit: submitText, busy } = COPY[mode] ?? COPY.create;
  const submitLabel = submitting ? busy : submitText;

  /* The two answers to "when is the guest coming?". A card that fixes its own
   * entry has neither to offer, and an edit is past the question. */
  const showArrivalTiles =
    !isEdit && arrival.options.allowNow && arrival.options.allowScheduled;

  return (
    <ModalCard
      title={heading}
      onClose={onClose}
      width={640}
      footerLeft={
        <button
          type="button"
          className={cx(ui.mutedBtn, s.small)}
          onClick={onClose}
        >
          Cancel
        </button>
      }
      footerRight={
        <button
          type="button"
          className={cx(ui.primaryBtn, s.small)}
          onClick={submit}
          disabled={!canSubmit}
        >
          {submitLabel}
        </button>
      }
    >
      {/* Nothing below the guest card means anything until the cards land, and
          a form that rearranges itself under the operator reads worse than a
          moment of waiting. */}
      {rateCardsLoading || archivedCardLoading ? (
        <div className={s.loading} aria-busy="true">
          <LoadingIndicator />
        </div>
      ) : (
        <>
          {submitError && <div className={s.errorBanner}>{submitError}</div>}

          {showArrivalTiles && (
            <div className={s.tiles} role="group" aria-label="Arrival">
              <button
                type="button"
                className={s.tile}
                aria-pressed={!isWalkUp}
                onClick={() => arrival.select(ARRIVAL.SCHEDULED)}
              >
                <div className={s.tileTitle}>Expected later</div>
                <div className={s.tileSub}>Shows under Expected arrivals</div>
              </button>
              <button
                type="button"
                className={s.tile}
                aria-pressed={isWalkUp}
                onClick={() => arrival.select(ARRIVAL.NOW)}
              >
                <div className={s.tileTitle}>At the desk now</div>
                <div className={s.tileSub}>Goes straight to the arrivals queue</div>
              </button>
            </div>
          )}

          <div className={s.grid2}>
            <FieldRow label="Licence plate" htmlFor="qb-plate">
              <input
                id="qb-plate"
                value={plate}
                onChange={(e) => setPlate(e.target.value)}
                placeholder="AAA123"
                className={cx(ui.field, s.plateInput)}
              />
            </FieldRow>

            {visibleFields.map((f) => (
              <FieldRow
                key={f.key}
                label={f.label}
                htmlFor={`qb-${f.key}`}
                hint={
                  f.phone && guest[f.key] && !isPossiblePhoneNumber(guest[f.key])
                    ? "Enter a mobile number this guest can be reached on."
                    : undefined
                }
              >
                {f.phone ? (
                  <PhoneField
                    value={guest[f.key]}
                    onChange={(next) => setGuestField(f.key, next)}
                    defaultCountry={countryCode}
                    placeholder={f.placeholder}
                    invalid={Boolean(guest[f.key]) && !isPossiblePhoneNumber(guest[f.key])}
                  />
                ) : (
                  <input
                    id={`qb-${f.key}`}
                    type={f.type ?? "text"}
                    value={guest[f.key]}
                    onChange={(e) => setGuestField(f.key, e.target.value)}
                    placeholder={f.placeholder}
                    className={ui.field}
                  />
                )}
              </FieldRow>
            ))}
          </div>

          <FieldRow
            label="Guest card"
            htmlFor="qb-card"
            hint={
              arrivalSettled
                ? "The guest is already on site, so the card this stay was sold on stands."
                : needsStripe
                  ? "This guest card requires a card on file — take it under Payment below."
                  : undefined
            }
          >
            <select
              id="qb-card"
              value={rateCard?.id ?? ""}
              onChange={(e) =>
                setRateCard(rateCards.find((c) => c.id === e.target.value) ?? null)
              }
              disabled={options.length === 0 || arrivalSettled}
              className={ui.field}
            >
              {/* The list opens on a card, so the prompt is only ever the answer
                  for a booking whose own card has been retired. */}
              {!rateCard && <option value="">Select a guest card</option>}
              {options.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
            {rateCardsError && (
              <p className={s.dangerNote}>Could not load guest cards.</p>
            )}
            {rateCardMissing && (
              <p className={s.warningNote}>
                {arrivalSettled
                  ? "This booking's guest card is no longer active, and the card cannot be changed once the guest is on site."
                  : "This booking's guest card is no longer active - pick a current one to save changes."}
              </p>
            )}
            {rateCard?.isArchived && (
              <p className={s.warningNote}>
                This booking's guest card is no longer active. It stands for this
                stay, and the rest of the booking can still be edited.
              </p>
            )}
          </FieldRow>

          {rateCard && (
            <>
              <FieldRow
                label="Payment"
                htmlFor="qb-payment"
                hint={
                  settledElsewhere
                    ? "Payment is not required for this type of guest card."
                    : payingLater
                      ? "No card is taken now. The guest adds one and settles from their pickup link before we release the vehicle."
                      : undefined
                }
              >
                {/* A card that settles elsewhere has nothing to choose between:
                    offering a disabled "card taken now" would misdescribe it. */}
                {settledElsewhere ? (
                  <p className={s.meta}>Nothing is taken at the desk.</p>
                ) : (
                  <select
                    id="qb-payment"
                    className={ui.field}
                    value={paymentMode}
                    disabled={paymentLocked}
                    onChange={(e) => setPaymentMode(e.target.value)}
                  >
                    <option value="onsite">
                      Paid on-site — card taken now at concierge
                    </option>
                    <option value="payLater" disabled={!allowPayLater}>
                      Guest pays in app — card captured before pickup
                    </option>
                  </select>
                )}
              </FieldRow>

              {showCardInput && (
                <FieldRow label="Card details">
                  <CardInput
                    appConfig={appConfig}
                    currency={installation.currency}
                    country={facility?.city?.country}
                    installationId={installation.id}
                    guestEmail={guest.guestEmail}
                    paymentMethod={paymentMethod}
                    confirmed={cardConfirmedThisSession}
                    readOnly={paymentLocked}
                    onConfirmed={(id) => {
                      setStripeCustomerId(id);
                      setCardConfirmedThisSession(true);
                      setPaymentMethod(null);
                      setCardRefresh((n) => n + 1);
                    }}
                  />
                </FieldRow>
              )}
            </>
          )}

          {rateCard && (
            <ArrivalSection
              arrival={arrival}
              isFixedPackage={isFixedPackage}
              showDates={Boolean(required.startDay)}
              startDay={startDay}
              tz={tz}
              now={now}
              minIso={arrivalFloor}
              allowPast={isEdit}
              locked={arrivalSettled}
            />
          )}

          {rateCard && (
            <DepartureSection
              departure={departure}
              isFixedPackage={isFixedPackage}
              isSingleDay={isSingleDay}
              showDates={Boolean(required.endDay)}
              endDay={endDay}
              setEndDay={setEndDay}
              tz={tz}
              now={now}
              minIso={arrivalInstant}
              allowPast={isEdit}
              allowUnknown={allowsUnknownDeparture(rateCard)}
            />
          )}

          {rateCard && (
            <>
              {/* Shown for every card, not just the ones with a scheduled fee: an
                  hourly booking still holds an amount at the desk. Both figures
                  come from the server - see useBookingQuote - and are only
                  formatted here. */}
              { !isEdit && (
                <FieldRow label="Charged now" hint={chargedNowHint}>
                  <div
                    className={cx(s.price, quoting && s.priceStale)}
                    aria-label="Charged now"
                  >
                    {formatCurrency(chargedNow, currency)}
                    <span className={s.priceMeta}> · {rateLine}</span>
                  </div>
                </FieldRow>
              )}

              <FieldRow label="Estimated total" hint={estimatedHint}>
                <div
                  className={cx(s.price, quoting && s.priceStale)}
                  aria-label="Estimated total"
                >
                  {hasEstimate ? formatCurrency(estimatedTotal, currency) : "—"}
                  <span className={s.priceMeta}> · {stayLine}</span>
                </div>
                {quoteError && <p className={s.dangerNote}>{quoteError}</p>}
              </FieldRow>
            </>
          )}

          <label className={s.check}>
            <input
              type="checkbox"
              checked={vip}
              onChange={(e) => setVip(e.target.checked)}
              className={s.checkbox}
            />
            VIP guest — page a contact on arrival
          </label>

          {vip &&
            (contacts.length === 0 ? (
              <p className={s.warningNote}>No booking contacts have been added yet.</p>
            ) : (
              <div className={s.vipRouting}>
                <span className={ui.eyebrow}>Calls</span>
                <VipContactPicker
                  contacts={contacts}
                  value={vipContactIds}
                  onChange={setVipContactIds}
                  pagedIds={pagedVipContactIds(initial)}
                  emptyMessage="No booking contacts have been added yet."
                />
              </div>
            ))}
        </>
      )}
    </ModalCard>
  );
}
